import { Container, Graphics } from 'pixi.js'
import { PORTAL_RADIUS, PORTAL_RELOCATE_INTERVAL } from '@star-snake/shared'
import { NeonColorsHex } from '../ui/neon-theme'
import type { ParticleManager } from '../vfx/ParticleManager'

/** Visual radius range: starts at 3x server radius, shrinks to 20 world units over the relocate interval. */
const PORTAL_VISUAL_MAX = PORTAL_RADIUS * 3
const PORTAL_VISUAL_MIN = 20

interface PortalState {
  x: number
  z: number
  timeLeft: number
  active: boolean
  radius: number
  relocating: boolean
}

interface EntryFlash {
  x: number
  z: number
  timer: number
  maxTime: number
}

export class PortalRenderer {
  private container: Container
  private portalGraphic: Graphics
  private glowGraphic: Graphics
  private rotationAngle = 0
  private particleManager: ParticleManager | null = null
  private frameCounter = 0

  // Relocation tracking
  private lastPortalX = 0
  private lastPortalZ = 0
  private hasLastPosition = false
  private relocating = false
  private relocateTimer = 0
  private relocateOldX = 0
  private relocateOldZ = 0
  private materializeTimer = 0

  // Entry flash effects
  private entryFlashes: EntryFlash[] = []

  constructor(parentContainer: Container) {
    this.container = new Container()
    parentContainer.addChild(this.container)

    this.glowGraphic = new Graphics()
    this.container.addChild(this.glowGraphic)

    this.portalGraphic = new Graphics()
    this.container.addChild(this.portalGraphic)
  }

  setParticleManager(pm: ParticleManager): void {
    this.particleManager = pm
  }

  triggerEntry(x: number, z: number): void {
    this.entryFlashes.push({ x, z, timer: 0.3, maxTime: 0.3 })
    this.particleManager?.emit('evacuation_warp', x, z)
  }

  update(portal: PortalState, dt: number): void {
    this.portalGraphic.clear()
    this.glowGraphic.clear()

    if (!portal.active) {
      this.container.visible = false
      return
    }

    this.container.visible = true
    this.rotationAngle += dt * 2
    this.frameCounter++

    const cx = portal.x
    const cz = portal.z

    // Detect relocation: position changed significantly
    this.updateRelocation(cx, cz, dt)

    // Calculate alpha/scale for materialize-in effect
    let alphaMultiplier = 1.0
    let scaleMultiplier = 1.0

    if (this.materializeTimer > 0) {
      this.materializeTimer = Math.max(0, this.materializeTimer - dt)
      const progress = 1 - this.materializeTimer / 0.5
      alphaMultiplier = progress
      scaleMultiplier = 0.5 + progress * 0.5
    }

    // Portal shrinks from 3x to min over the relocate interval
    const timeFraction = Math.max(0, Math.min(1, portal.timeLeft / PORTAL_RELOCATE_INTERVAL))
    const baseVisualRadius = PORTAL_VISUAL_MIN + (PORTAL_VISUAL_MAX - PORTAL_VISUAL_MIN) * timeFraction
    const visualRadius = baseVisualRadius * scaleMultiplier

    // Draw relocate fade-out at old position
    if (this.relocating) {
      this.drawRelocateFadeOut(dt)
    }

    // Outer glow (large soft circle behind rings)
    this.glowGraphic.circle(cx, cz, visualRadius * 1.5)
    this.glowGraphic.fill({ color: NeonColorsHex.portal, alpha: 0.1 * alphaMultiplier })

    // Dark portal body
    this.portalGraphic.circle(cx, cz, visualRadius)
    this.portalGraphic.fill({ color: 0x110033, alpha: 0.9 * alphaMultiplier })

    // VFX-10: Rotating concentric rings at different speeds
    this.drawRotatingRings(cx, cz, visualRadius, alphaMultiplier)

    // Core: bright white center with size oscillation
    const coreOscillation = 1 + Math.sin(Date.now() * 0.005) * 0.15
    const coreRadius = visualRadius * 0.2 * coreOscillation
    this.portalGraphic.circle(cx, cz, coreRadius)
    this.portalGraphic.fill({ color: NeonColorsHex.textBright, alpha: 0.6 * alphaMultiplier })

    // VFX-10: Inward-pull particles (cap 2 per frame, every ~10 frames)
    if (this.frameCounter % 10 === 0 && this.particleManager) {
      const edgeAngle = Math.random() * Math.PI * 2
      const edgeX = cx + Math.cos(edgeAngle) * visualRadius
      const edgeZ = cz + Math.sin(edgeAngle) * visualRadius
      this.particleManager.emit('portal_vortex', edgeX, edgeZ)
    }

    // Time indicator: faster pulsing as time runs out
    if (portal.timeLeft < 15 && !portal.relocating) {
      const urgency = 1 - portal.timeLeft / 15  // 0 at 15s, 1 at 0s
      const pulseSpeed = 0.005 + urgency * 0.02  // faster as time runs out
      const flash = 0.3 + (0.5 + Math.sin(Date.now() * pulseSpeed) * 0.5) * 0.7
      this.portalGraphic.circle(cx, cz, visualRadius + 4)
      this.portalGraphic.stroke({ color: 0xFFAA00, width: 2 + urgency * 3, alpha: flash })
    }

    // Final flash burst when about to relocate (last 1 second)
    if (portal.timeLeft < 1 && portal.timeLeft > 0 && !portal.relocating) {
      const burstAlpha = 1 - portal.timeLeft  // 0→1 as time approaches 0
      this.glowGraphic.circle(cx, cz, visualRadius * 2)
      this.glowGraphic.fill({ color: NeonColorsHex.textBright, alpha: burstAlpha * 0.4 })
    }

    // VFX-12: Render entry flashes
    this.updateEntryFlashes(dt)

    // Store last known position
    this.lastPortalX = cx
    this.lastPortalZ = cz
    this.hasLastPosition = true
  }

  private drawRotatingRings(
    cx: number,
    cz: number,
    visualRadius: number,
    alphaMultiplier: number,
  ): void {
    const ringCount = 4
    for (let r = 0; r < ringCount; r++) {
      const ringRadius = visualRadius * (0.45 + r * 0.18)
      const ringAlpha = (0.7 - r * 0.12) * alphaMultiplier
      const speed = Date.now() * 0.002 * (r + 1)
      const offset = speed + (r * Math.PI) / 2

      // Draw 3 arc segments per ring (not full circle)
      for (let seg = 0; seg < 3; seg++) {
        const startAngle = offset + (seg * Math.PI * 2) / 3
        const endAngle = startAngle + Math.PI / 5

        const startX = cx + Math.cos(startAngle) * ringRadius
        const startZ = cz + Math.sin(startAngle) * ringRadius

        this.portalGraphic.moveTo(startX, startZ)
        this.portalGraphic.arc(cx, cz, ringRadius, startAngle, endAngle)
        this.portalGraphic.stroke({
          color: NeonColorsHex.portal,
          width: 2,
          alpha: ringAlpha,
        })
      }
    }
  }

  private updateRelocation(cx: number, cz: number, dt: number): void {
    if (!this.hasLastPosition) return

    const dx = cx - this.lastPortalX
    const dz = cz - this.lastPortalZ
    const distSq = dx * dx + dz * dz

    // Position changed significantly (more than 10 units)
    if (distSq > 100) {
      this.relocating = true
      this.relocateTimer = 0.5
      this.relocateOldX = this.lastPortalX
      this.relocateOldZ = this.lastPortalZ
      this.materializeTimer = 0.5

      // Emit burst at old position
      this.particleManager?.emit('portal_vortex', this.relocateOldX, this.relocateOldZ)
    }

    if (this.relocating) {
      this.relocateTimer -= dt
      if (this.relocateTimer <= 0) {
        this.relocating = false
      }
    }
  }

  private drawRelocateFadeOut(dt: number): void {
    const progress = this.relocateTimer / 0.5
    const fadeAlpha = progress

    // Flash at old position (fading out)
    this.glowGraphic.circle(this.relocateOldX, this.relocateOldZ, 80 * (1 + (1 - progress) * 0.5))
    this.glowGraphic.fill({ color: NeonColorsHex.textBright, alpha: fadeAlpha * 0.5 })

    // Shrinking ring at old position
    const ringRadius = 60 * progress
    this.glowGraphic.circle(this.relocateOldX, this.relocateOldZ, ringRadius)
    this.glowGraphic.stroke({ color: NeonColorsHex.portal, width: 3, alpha: fadeAlpha })
  }

  private updateEntryFlashes(dt: number): void {
    let i = 0
    while (i < this.entryFlashes.length) {
      const flash = this.entryFlashes[i]
      flash.timer -= dt

      if (flash.timer <= 0) {
        this.entryFlashes.splice(i, 1)
        continue
      }

      const progress = 1 - flash.timer / flash.maxTime
      const flashRadius = 30 + progress * 120
      const flashAlpha = 1 - progress

      // Expanding white flash circle
      this.portalGraphic.circle(flash.x, flash.z, flashRadius)
      this.portalGraphic.fill({ color: NeonColorsHex.textBright, alpha: flashAlpha * 0.8 })

      // Expanding neon ring
      this.portalGraphic.circle(flash.x, flash.z, flashRadius * 0.8)
      this.portalGraphic.stroke({ color: NeonColorsHex.portal, width: 3, alpha: flashAlpha })

      i++
    }
  }

  dispose(): void {
    this.portalGraphic.destroy()
    this.glowGraphic.destroy()
    this.container.parent?.removeChild(this.container)
    this.container.destroy()
  }
}
